import { Camera, MathUtils, Raycaster, Vector2, Vector3, Plane } from "three";
import {
  OrbitControls,
  type OrbitControlsEventMap,
} from "three/examples/jsm/controls/OrbitControls.js";
import type { ICollisionSystem } from "../collision/colliderTypes";

const isTouchDevice =
  "ontouchstart" in globalThis || navigator.maxTouchPoints > 0;

const _scratchDesiredPosition = new Vector3();
const _scratchNextPosition = new Vector3();
const _scratchSubdivCurrent = new Vector3();
const _scratchSubdivTarget = new Vector3();

const MAX_COLLISION_STEP = 0.15;
const MAX_SUBDIVISION_STEPS = 8;
const PAN_VELOCITY_EPSILON = 1e-5;

/** Slide disabled — orbit already provides angular movement; sliding amplifies it. */
const ORBIT_COLLISION_OPTIONS = { slide: false } as const;

export type PanMode = 'screenSpace' | 'groundPlane';

export type PanBoundsXZ = {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
};

export class OrbitCameraControls {
  // angle / distance limits ------------------------------------------------
  minDistance: number = 0;
  maxDistance: number = Infinity;
  minPolarAngle: number = 0;
  maxPolarAngle: number = Math.PI;
  minAzimuthAngle: number = -Infinity;
  maxAzimuthAngle: number = Infinity;
  // height / pan-cage clamps ----------------------------------------------
  clampMinHeight: number = -Infinity;
  clampMaxHeight: number = Infinity;
  clampMaxHeightFar: number = Infinity;
  panBoundsXZ: PanBoundsXZ | null = null;
  // pan / inertia / touch behaviour ---------------------------------------
  panMode: PanMode = 'screenSpace';
  groundPlaneY: number = 0;
  forcePanOnTouch: boolean = false;
  panSpeed: number = 1.0;
  inertiaFactor: number = 0.88;
  /**
   * Multiplier passed to Three.js OrbitControls `zoomSpeed`. Higher values
   * give faster zoom per wheel/pinch tick. Default 0.8 (suitable for
   * detail-scenes like kaaba/arafat). For restricted top-down scenes use
   * ~4 to match the responsiveness of an exponential-factor wheel handler.
   * Applied via `applyLimits()`.
   */
  zoomSpeed: number = 0.8;
  // collision integration -------------------------------------------------
  collisionSystem: ICollisionSystem | null = null;

  private controls: OrbitControls;
  private lastSafePosition: Vector3 = new Vector3();
  private connectedElement: HTMLElement | null = null;
  /**
   * When true, ALL pose-restricting clamps are bypassed:
   *   - Three.js OrbitControls' polar/azimuth/distance clamps (we write
   *     wide-open values via `applyLimits()`).
   *   - `panBoundsXZ` post-update cage (skipped in `applyConstraints()`).
   *   - `target.y = groundPlaneY` lock in `update()` (skipped).
   *
   * Field values themselves are preserved — suspension only gates what
   * gets written to Three.js' internal limits. Toggle via
   * `suspendLocks()` / `restoreLocks()` (typically driven by createScene
   * lifecycle: suspended during preview-tour + flyover, restored at
   * `sceneLoadingFinished`).
   */
  private _locksSuspended: boolean = false;

  // ground-plane pan internals -------------------------------------------
  private gpPanRaycaster: Raycaster | null = null;
  private gpPanPlane: Plane | null = null;
  private gpPanPointers: Map<number, { x: number; y: number }> = new Map();
  private gpPanLastSinglePointer: { x: number; y: number } | null = null;
  private gpPanLastPinchMidpoint: { x: number; y: number } | null = null;
  private gpPanIsDragging: boolean = false;
  private gpPanVelocity: Vector3 = new Vector3();
  private gpPanScratchVec3A: Vector3 = new Vector3();
  private gpPanScratchVec3B: Vector3 = new Vector3();
  private gpPanScratchVec2: Vector2 = new Vector2();
  private gpPanHandlersAttached: boolean = false;

  private readonly handleGpPointerDown: (e: PointerEvent) => void;
  private readonly handleGpPointerMove: (e: PointerEvent) => void;
  private readonly handleGpPointerUp: (e: PointerEvent) => void;
  private readonly handleGpPointerLeave: () => void;
  private readonly handleGpWindowBlur: () => void;

  constructor(object: Camera, domElement: HTMLElement | null = null) {
    this.controls = new OrbitControls(object, domElement ?? undefined);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.12;
    this.controls.enablePan = !isTouchDevice;
    this.controls.rotateSpeed = 0.6;
    // `zoomSpeed` is applied via `applyLimits()` (driven by SceneControls'
    // applyCameraConfig pipeline) — initialised here to the same default as
    // the public field so behaviour pre-applyCameraConfig matches the field.
    this.controls.zoomSpeed = this.zoomSpeed;
    this.controls.enabled = false;
    this.lastSafePosition.copy(object.position);

    this.gpPanRaycaster = new Raycaster();
    // Plane equation: n·p + d = 0. With n = (0,1,0) and target plane y = Y,
    // we need d = -Y. The plane is updated whenever groundPlaneY changes
    // (see `refreshPanMode`).
    this.gpPanPlane = new Plane(new Vector3(0, 1, 0), 0);

    this.handleGpPointerDown = this.onGpPointerDown.bind(this);
    this.handleGpPointerMove = this.onGpPointerMove.bind(this);
    this.handleGpPointerUp = this.onGpPointerUp.bind(this);
    this.handleGpPointerLeave = this.onGpPointerLeave.bind(this);
    this.handleGpWindowBlur = this.onGpPointerLeave.bind(this);
  }

  get enabled(): boolean {
    return this.controls.enabled;
  }

  set enabled(value: boolean) {
    this.controls.enabled = value;
    if (!value) {
      // Releasing controls (e.g. during flyover) should immediately drop
      // any pending ground-plane drag/inertia state — stale velocity
      // would otherwise be applied on the first frame after re-enable.
      this.gpPanPointers.clear();
      this.gpPanLastSinglePointer = null;
      this.gpPanLastPinchMidpoint = null;
      this.gpPanIsDragging = false;
      this.gpPanVelocity.set(0, 0, 0);
    }
  }

  get target(): Vector3 {
    return this.controls.target;
  }

  addEventListener<K extends keyof OrbitControlsEventMap>(
    type: K,
    listener: (event: OrbitControlsEventMap[K]) => void
  ): void {
    this.controls.addEventListener(type, listener);
  }

  removeEventListener<K extends keyof OrbitControlsEventMap>(
    type: K,
    listener: (event: OrbitControlsEventMap[K]) => void
  ): void {
    this.controls.removeEventListener(type, listener);
  }

  connect(element: HTMLElement): void {
    this.controls.connect(element);
    this.connectedElement = element;
    this.refreshPanMode();
  }

  disconnect(): void {
    this.detachGpPanHandlers();
    this.controls.disconnect();
    this.connectedElement = null;
  }

  dispose(): void {
    this.detachGpPanHandlers();
    this.controls.dispose();
    // Etap #11/#14 of CLAUDE.md — null heavy references on dispose so
    // JSC can GC the entire scene infrastructure independently of any
    // closures retaining this instance.
    this.gpPanRaycaster = null;
    this.gpPanPlane = null;
    this.gpPanPointers.clear();
    this.gpPanLastSinglePointer = null;
    this.gpPanLastPinchMidpoint = null;
  }

  setTarget(target: Vector3): void {
    this.controls.target.copy(target);
    if (this.panMode === 'groundPlane') {
      this.controls.target.y = this.groundPlaneY;
    }
    this.controls.update();
    this.lastSafePosition.copy(this.controls.object.position);
  }

  applyLimits(): void {
    if (this._locksSuspended) {
      // Suspended: write wide-open Three.js OrbitControls limits so the
      // preloader animation (preview-tour, flyover) can move the camera
      // anywhere — useful for staging horizontal cinematic poses on a
      // restricted top-down scene that locks azimuth/polar at runtime.
      this.controls.minDistance = 0;
      this.controls.maxDistance = Infinity;
      this.controls.minPolarAngle = 0;
      this.controls.maxPolarAngle = Math.PI;
      this.controls.minAzimuthAngle = -Infinity;
      this.controls.maxAzimuthAngle = Infinity;
    } else {
      this.controls.minDistance = this.minDistance;
      this.controls.maxDistance = this.maxDistance;
      this.controls.minPolarAngle = this.minPolarAngle;
      this.controls.maxPolarAngle = this.maxPolarAngle;
      this.controls.minAzimuthAngle = this.minAzimuthAngle;
      this.controls.maxAzimuthAngle = this.maxAzimuthAngle;
    }
    this.controls.zoomSpeed = this.zoomSpeed;
  }

  get locksSuspended(): boolean {
    return this._locksSuspended;
  }

  /**
   * Suspend pose-restricting clamps (azimuth/polar/distance/cage/groundPlaneY).
   * Field values are preserved; only what gets written to Three.js internals
   * changes. `restoreLocks()` reverses. Idempotent.
   */
  suspendLocks(): void {
    if (this._locksSuspended) return;
    this._locksSuspended = true;
    this.applyLimits();
  }

  /**
   * Restore pose-restricting clamps to their configured field values.
   * Camera will be re-clamped on next `update()` — author preview-tour
   * end / flyover finish poses to be already-compatible to avoid a
   * visible snap.
   */
  restoreLocks(): void {
    if (!this._locksSuspended) return;
    this._locksSuspended = false;
    this.applyLimits();
  }

  /**
   * Re-evaluate pan-mode-specific Three.js OrbitControls flags and
   * (re)attach the custom ground-plane pointer handlers. Idempotent.
   * Called from `connect()` and from `SceneControls.applyCameraConfig`
   * whenever the merged config might have changed `panMode`,
   * `groundPlaneY`, or `forcePanOnTouch`.
   */
  refreshPanMode(): void {
    if (this.panMode === 'groundPlane') {
      // In ground-plane mode the custom handler owns ALL pan input; the
      // native screen-space pan must stay disabled. Rotation is also
      // disabled here — restricted scenes typically lock azimuth/polar
      // anyway, and leaving native rotate enabled would race with the
      // custom pointerdown handler for the same gesture (both attach to
      // the same element). Zoom remains enabled so wheel + pinch keep
      // working through Three.js' native code path.
      this.controls.enablePan = false;
      this.controls.enableRotate = false;
      this.controls.enableZoom = true;
      this.attachGpPanHandlers();
    } else {
      this.controls.enablePan = this.forcePanOnTouch ? true : !isTouchDevice;
      this.controls.enableRotate = true;
      this.controls.enableZoom = true;
      this.detachGpPanHandlers();
    }
    if (this.gpPanPlane) {
      // Plane y = groundPlaneY → n=(0,1,0), d = -groundPlaneY.
      this.gpPanPlane.normal.set(0, 1, 0);
      this.gpPanPlane.constant = -this.groundPlaneY;
    }
  }

  syncPosition(): void {
    this.lastSafePosition.copy(this.controls.object.position);
  }

  update(): void {
    // Apply ground-plane pan inertia BEFORE Three.js update so the new
    // target is used in the same frame to recompute camera position.
    if (this.panMode === 'groundPlane' && !this.gpPanIsDragging) {
      const v = this.gpPanVelocity;
      if (
        Math.abs(v.x) > PAN_VELOCITY_EPSILON
        || Math.abs(v.z) > PAN_VELOCITY_EPSILON
      ) {
        const t = this.controls.target;
        t.x += v.x;
        t.z += v.z;
        // Move camera by the same delta so distance/azimuth/polar are
        // preserved across the pan step (Three.js' update() will do it
        // too, but we apply it explicitly so the inertial path is
        // robust against damping smoothing.)
        this.controls.object.position.x += v.x;
        this.controls.object.position.z += v.z;
        v.multiplyScalar(this.inertiaFactor);
      } else if (v.lengthSq() > 0) {
        v.set(0, 0, 0);
      }
    }
    this.controls.update();
    if (this.panMode === 'groundPlane' && !this._locksSuspended) {
      // Lock target.y to groundPlaneY — any drift from rotation damping
      // or numerical error would gradually tilt the camera off the
      // canonical pose. Skipped when suspended so preloader animations
      // can drive a horizontal/free camera through arbitrary y values.
      this.controls.target.y = this.groundPlaneY;
    }
    this.applyConstraints();
  }

  rotateLeft(angle: number): boolean {
    const orbitControls =
      this.controls as OrbitControls & { rotateLeft?: (angle: number) => void };
    if (typeof orbitControls.rotateLeft !== "function") {
      return false;
    }
    orbitControls.rotateLeft.call(this.controls, angle);
    return true;
  }

  setAutoRotate(enabled: boolean, speed: number): void {
    this.controls.autoRotate = enabled;
    this.controls.autoRotateSpeed = speed;
  }

  /**
   * Drain Three.js OrbitControls' internal `sphericalDelta` to zero without
   * moving the camera. Three.js accumulates pending rotational velocity when
   * `setAutoRotate` is enabled or `rotateLeft` is called; that delta is
   * applied (and damped) on every `update()`. If `update()` is skipped for a
   * while (e.g. while `enabled === false` during a pose transition), the
   * accumulated delta is applied in one frame the moment update() resumes,
   * shifting the camera off its expected pose. This method short-circuits
   * that by:
   *   1. Disabling auto-rotate (no new delta added).
   *   2. Saving current camera position.
   *   3. Running update() with damping=false (Three.js zeroes sphericalDelta
   *      at end of update() in non-damping mode).
   *   4. Restoring camera position so any movement caused by the drain step
   *      is invisible. Internal spherical state will re-sync with camera
   *      position on the next update() call.
   */
  freezeMomentum(): void {
    this.controls.autoRotate = false;
    this.controls.autoRotateSpeed = 0;
    const savedPosition = this.controls.object.position.clone();
    const wasEnabled = this.controls.enabled;
    const wasDamping = this.controls.enableDamping;
    this.controls.enabled = true;
    this.controls.enableDamping = false;
    this.controls.update();
    this.controls.enableDamping = wasDamping;
    this.controls.enabled = wasEnabled;
    this.controls.object.position.copy(savedPosition);
    this.gpPanVelocity.set(0, 0, 0);
  }

  // ---------------------------------------------------------------------
  // ground-plane pan implementation
  // ---------------------------------------------------------------------

  private attachGpPanHandlers(): void {
    if (this.gpPanHandlersAttached) return;
    const el = this.connectedElement;
    if (!el) return;
    el.addEventListener("pointerdown", this.handleGpPointerDown);
    el.addEventListener("pointermove", this.handleGpPointerMove);
    el.addEventListener("pointerup", this.handleGpPointerUp);
    el.addEventListener("pointercancel", this.handleGpPointerUp);
    el.addEventListener("pointerleave", this.handleGpPointerLeave);
    window.addEventListener("blur", this.handleGpWindowBlur);
    this.gpPanHandlersAttached = true;
  }

  private detachGpPanHandlers(): void {
    if (!this.gpPanHandlersAttached) return;
    const el = this.connectedElement;
    if (el) {
      el.removeEventListener("pointerdown", this.handleGpPointerDown);
      el.removeEventListener("pointermove", this.handleGpPointerMove);
      el.removeEventListener("pointerup", this.handleGpPointerUp);
      el.removeEventListener("pointercancel", this.handleGpPointerUp);
      el.removeEventListener("pointerleave", this.handleGpPointerLeave);
    }
    window.removeEventListener("blur", this.handleGpWindowBlur);
    this.gpPanPointers.clear();
    this.gpPanLastSinglePointer = null;
    this.gpPanLastPinchMidpoint = null;
    this.gpPanIsDragging = false;
    this.gpPanVelocity.set(0, 0, 0);
    this.gpPanHandlersAttached = false;
  }

  private resolvePlanePoint(
    clientX: number,
    clientY: number,
    target: Vector3,
  ): Vector3 | null {
    const el = this.connectedElement;
    const raycaster = this.gpPanRaycaster;
    const plane = this.gpPanPlane;
    if (!el || !raycaster || !plane) return null;
    const rect = el.getBoundingClientRect();
    const x = ((clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1;
    const y = -((clientY - rect.top) / Math.max(rect.height, 1)) * 2 + 1;
    this.gpPanScratchVec2.set(x, y);
    raycaster.setFromCamera(this.gpPanScratchVec2, this.controls.object as Camera);
    return raycaster.ray.intersectPlane(plane, target);
  }

  private applyGpPanDelta(deltaX: number, deltaZ: number): void {
    if (!Number.isFinite(deltaX) || !Number.isFinite(deltaZ)) return;
    const dx = deltaX * this.panSpeed;
    const dz = deltaZ * this.panSpeed;
    const t = this.controls.target;
    t.x += dx;
    t.z += dz;
    // Move camera by the same delta so distance/azimuth/polar are
    // preserved (Three.js' update() will also derive position from
    // target+spherical, but applying explicitly avoids a single-frame
    // ghosted lookAt on slow updates).
    this.controls.object.position.x += dx;
    this.controls.object.position.z += dz;
    this.gpPanVelocity.set(dx, 0, dz);
  }

  private onGpPointerDown(event: PointerEvent): void {
    if (!this.controls.enabled) return;
    if (event.button !== 0 && event.pointerType !== "touch") return;
    this.connectedElement?.setPointerCapture(event.pointerId);
    this.gpPanPointers.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });
    if (this.gpPanPointers.size === 1) {
      this.gpPanLastSinglePointer = { x: event.clientX, y: event.clientY };
      this.gpPanLastPinchMidpoint = null;
    } else if (this.gpPanPointers.size === 2) {
      const pts = Array.from(this.gpPanPointers.values());
      this.gpPanLastPinchMidpoint = {
        x: (pts[0].x + pts[1].x) * 0.5,
        y: (pts[0].y + pts[1].y) * 0.5,
      };
      this.gpPanLastSinglePointer = null;
    }
    this.gpPanIsDragging = true;
    this.gpPanVelocity.set(0, 0, 0);
    // Native OrbitControls fires 'start' on its own pointerdown when
    // any of rotate/pan/zoom is enabled. With rotate+pan disabled in
    // ground-plane mode, native handler still fires (zoom is enabled),
    // BUT only on wheel/pinch — left-click pointerdown won't trigger
    // it. Manually dispatch so SceneControls.handleOrbitInteractionStart
    // (sight stop / requestRender / orbitAutoMove off) keeps working.
    this.controls.dispatchEvent({ type: "start" });
  }

  private onGpPointerMove(event: PointerEvent): void {
    if (!this.controls.enabled) return;
    if (!this.gpPanPointers.has(event.pointerId)) return;
    this.gpPanPointers.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });
    if (this.gpPanPointers.size === 1) {
      const last = this.gpPanLastSinglePointer;
      if (!last) return;
      const prev = this.resolvePlanePoint(last.x, last.y, this.gpPanScratchVec3A);
      const next = this.resolvePlanePoint(event.clientX, event.clientY, this.gpPanScratchVec3B);
      if (!prev || !next) return;
      this.applyGpPanDelta(prev.x - next.x, prev.z - next.z);
      this.gpPanLastSinglePointer = { x: event.clientX, y: event.clientY };
    } else if (this.gpPanPointers.size >= 2) {
      const pts = Array.from(this.gpPanPointers.values());
      const mid = {
        x: (pts[0].x + pts[1].x) * 0.5,
        y: (pts[0].y + pts[1].y) * 0.5,
      };
      const lastMid = this.gpPanLastPinchMidpoint;
      if (lastMid) {
        const prev = this.resolvePlanePoint(lastMid.x, lastMid.y, this.gpPanScratchVec3A);
        const next = this.resolvePlanePoint(mid.x, mid.y, this.gpPanScratchVec3B);
        if (prev && next) {
          this.applyGpPanDelta(prev.x - next.x, prev.z - next.z);
        }
      }
      this.gpPanLastPinchMidpoint = mid;
    }
  }

  private onGpPointerUp(event: PointerEvent): void {
    this.gpPanPointers.delete(event.pointerId);
    if (this.gpPanPointers.size === 0) {
      this.gpPanLastSinglePointer = null;
      this.gpPanLastPinchMidpoint = null;
      this.gpPanIsDragging = false;
      this.controls.dispatchEvent({ type: "end" });
    } else if (this.gpPanPointers.size === 1) {
      // After releasing one of two fingers — switch from pinch-mode
      // back to single-finger pan mode using the remaining pointer's
      // last known position as anchor.
      const remaining = Array.from(this.gpPanPointers.values())[0];
      this.gpPanLastSinglePointer = { x: remaining.x, y: remaining.y };
      this.gpPanLastPinchMidpoint = null;
    }
  }

  private onGpPointerLeave(): void {
    if (this.gpPanPointers.size === 0 && !this.gpPanIsDragging) return;
    this.gpPanPointers.clear();
    this.gpPanLastSinglePointer = null;
    this.gpPanLastPinchMidpoint = null;
    this.gpPanIsDragging = false;
    this.controls.dispatchEvent({ type: "end" });
  }

  // ---------------------------------------------------------------------
  // height / collision / pan-cage post-update clamps
  // ---------------------------------------------------------------------

  private computeEffectiveMaxHeight(): number {
    if (this.clampMaxHeight === Infinity) {
      return Infinity;
    }

    if (this.clampMaxHeightFar >= this.clampMaxHeight || this.maxDistance <= this.minDistance) {
      return this.clampMaxHeight;
    }

    const distance = this.controls.object.position.distanceTo(this.controls.target);
    const t = MathUtils.clamp(
      (distance - this.minDistance) / (this.maxDistance - this.minDistance),
      0,
      1
    );

    return MathUtils.lerp(this.clampMaxHeight, this.clampMaxHeightFar, t);
  }

  private applyConstraints(): void {
    const desiredPosition = _scratchDesiredPosition.copy(this.controls.object.position);
    const constrainedPosition = _scratchNextPosition.copy(desiredPosition);

    if (this.collisionSystem) {
      const totalDist = this.lastSafePosition.distanceTo(desiredPosition);

      if (totalDist > MAX_COLLISION_STEP) {
        const steps = Math.min(Math.ceil(totalDist / MAX_COLLISION_STEP), MAX_SUBDIVISION_STEPS);
        const current = _scratchSubdivCurrent.copy(this.lastSafePosition);

        for (let i = 1; i <= steps; i++) {
          const t = i / steps;
          const target = _scratchSubdivTarget.lerpVectors(
            this.lastSafePosition,
            desiredPosition,
            t
          );
          const safe = this.collisionSystem.getSafePosition(current, target, ORBIT_COLLISION_OPTIONS);
          if (safe.distanceTo(current) < this.collisionSystem.getConfig().epsilon) {
            break;
          }
          current.copy(safe);
        }

        constrainedPosition.copy(current);
      } else {
        constrainedPosition.copy(
          this.collisionSystem.getSafePosition(this.lastSafePosition, desiredPosition, ORBIT_COLLISION_OPTIONS)
        );
      }
    }

    const effectiveMaxHeight = this.computeEffectiveMaxHeight();

    if (this.clampMinHeight !== -Infinity || effectiveMaxHeight !== Infinity) {
      constrainedPosition.y = MathUtils.clamp(
        constrainedPosition.y,
        this.clampMinHeight,
        effectiveMaxHeight
      );
    }

    if (!constrainedPosition.equals(desiredPosition)) {
      this.controls.object.position.copy(constrainedPosition);
      this.controls.object.lookAt(this.controls.target);
    }

    // Pan-cage clamp — keep target inside `panBoundsXZ` and shift the
    // camera by the same delta so distance/azimuth/polar are preserved.
    // Only X/Z axes; target.y is locked separately by `update()` in
    // ground-plane mode. Skipped during lock-suspension so preloader
    // animations can position camera/target anywhere.
    const cage = this.panBoundsXZ;
    if (cage && !this._locksSuspended) {
      const t = this.controls.target;
      const clampedX = MathUtils.clamp(t.x, cage.minX, cage.maxX);
      const clampedZ = MathUtils.clamp(t.z, cage.minZ, cage.maxZ);
      const dx = clampedX - t.x;
      const dz = clampedZ - t.z;
      if (dx !== 0 || dz !== 0) {
        t.x = clampedX;
        t.z = clampedZ;
        this.controls.object.position.x += dx;
        this.controls.object.position.z += dz;
        // Cancel velocity component along the clamped axis so inertia
        // doesn't continuously fight the cage.
        if (dx !== 0) this.gpPanVelocity.x = 0;
        if (dz !== 0) this.gpPanVelocity.z = 0;
      }
    }

    this.lastSafePosition.copy(this.controls.object.position);
  }
}
